import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { bellStateId } from "$global-providers/notifications-provider/deterministic-ids";
import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type { NotificationBellStateInput } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { NotificationBellState as RxNotificationBellState } from "../collections/notification-bell-state";
import type { RxDBCollections } from "../provider";
import {
  SyncNotificationBellStatePullDocument,
  SyncNotificationBellStatePushDocument,
  type WholeSyncableNotificationBellStateFragment,
} from "./__generated__/notification-bell-state.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateNotificationBellState = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxNotificationBellState>;
}) => {
  const [flagEnabled] = useFeatureFlag<boolean>(
    featureFlags.notifications.center,
  );
  // Replication is per-user; shared-device installs need a user-scoped
  // checkpoint so a later user can't inherit a previous user's pull cursor
  // (see Copilot review on PR #2115).
  const user = useLazyUser();
  const userId = user?.id;
  const enabled = flagEnabled && !!userId;

  const pull = useMemo<
    | ReplicationPullConfig<WholeSyncableNotificationBellStateFragment>
    | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncNotificationBellStatePullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  const push = useMemo(
    () =>
      enabled && userId
        ? {
            mutation: SyncNotificationBellStatePushDocument,
            mapRxToGql: mapRxNotificationBellStateToGqlInput,
            // PCW keeps RxDB across logout (see project_rxdb_facility_cache_design).
            // The shared collection may hold other users' bell-state rows; this
            // replication identity belongs to `userId` and must only push that
            // user's rows. Rows for other users stay on disk for their own
            // replication identity to handle.
            filter: (doc: { userId: string }) => doc.userId === userId,
          }
        : undefined,
    [enabled, userId],
  );

  return useGqlReplication<
    RxNotificationBellState,
    WholeSyncableNotificationBellStateFragment,
    NotificationBellStateInput
  >({
    db,
    collection,
    pull,
    push,
    mapGqlToRx: mapGqlNotificationBellStateToRx,
    batchSize: 100,
    metadataLocalStorageKeySuffix: `notification-bell-state-${userId ?? "anonymous"}`,
    replicationIdentifierSuffix: userId,
    enabled,
  });
};

export const mapGqlNotificationBellStateToRx = (
  doc: WholeSyncableNotificationBellStateFragment,
): WithDeleted<RxNotificationBellState> => ({
  // Rewrite id to the deterministic `bellstate-${userId}` so multi-device
  // pulls converge on a single row regardless of what id the server stored
  // historically (rows created before the deterministic-id rollout used a
  // random uuid).
  id: bellStateId(doc.userId),
  userId: doc.userId,
  lastBellOpenedAt: doc.lastBellOpenedAt ?? null,
  lastMarkAllReadAt: doc.lastMarkAllReadAt ?? null,
  meta: {
    createdAt: doc.meta.createdAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedAt: doc.meta.updatedAt,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

export const mapRxNotificationBellStateToGqlInput = (
  doc: WithDeleted<RxNotificationBellState>,
): NotificationBellStateInput => ({
  id: doc.id,
  userId: doc.userId,
  lastBellOpenedAt: doc.lastBellOpenedAt ?? null,
  lastMarkAllReadAt: doc.lastMarkAllReadAt ?? null,
  meta: {
    createdAt: doc.meta.createdAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedAt: doc.meta.updatedAt,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableNotificationBellState on NotificationBellState {
    id
    userId
    lastBellOpenedAt
    lastMarkAllReadAt
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncNotificationBellStatePull(
    $input: PullNotificationBellStateInput!
  ) {
    pullNotificationBellState(input: $input) {
      documents {
        ...WholeSyncableNotificationBellState
      }
    }
  }
`;

gql`
  mutation SyncNotificationBellStatePush(
    $input: PushNotificationBellStateInput!
  ) {
    pushNotificationBellState(input: $input) {
      rows {
        ...WholeSyncableNotificationBellState
      }
    }
  }
`;
